import React, {useState, useEffect} from "react";
import {useNavigate} from "react-router-dom";
import {Checkbox, FormControlLabel} from "@mui/material";
import {styled} from "@mui/system";
import moment from "moment";
import Swal from "sweetalert2";
import {StartExam} from "../../utils/axios/questionAPI";
import {GetUserInfo} from "../../utils/helper";
import StartButton from "../Common/StartButton";
import backgroundVideo from "../../assets/images/gif-2.mp4";

//Video as Background
const VideoBackground = styled("video")({
  position: "fixed",
  top: 0,
  left: 0,
  width: "100%",
  height: "100%",
  objectFit: "cover",
  zIndex: 0,
  opacity: 0.9,
});

const Overlay = styled("div")({
  position: "fixed",
  top: 0,
  left: 0,
  width: "100%",
  height: "100%",
  backgroundColor: "rgba(0, 0, 0, 0.5)", //dark overlay settings
  zIndex: 1,
});

const Container = styled("div")({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "flex-start",
  backgroundColor: "transparent",
  zIndex: 2,
  position: "absolute",
  top: 0,
  left: 0,
  width: "100%",
  height: "100vh",
  padding: "clamp(10px, 4vh, 40px) 0",
  boxSizing: "border-box",
  overflowY: "auto",
  scrollbarWidth: "thin",
  scrollbarColor: "rgba(255,255,255,0.6) transparent",
});

const DialogContentStyled = styled("div")({
  backgroundColor: "rgba(0, 0, 0, 0.4)",
  padding: "clamp(15px, 2vh, 30px) clamp(20px, 2vw, 40px)",
  borderRadius: "12px",
  color: "white",
  width: "90%",
  maxWidth: "clamp(600px, 60vw, 900px)",
  maxHeight: "clamp(400px, 75vh, 800px)",
  overflowY: "auto",
  textAlign: "justify",
  lineHeight: "1.5",
  marginBottom: "clamp(10px, 2vh, 25px)",
  boxSizing: "border-box",
  fontSize: "clamp(13px, 1.2vw, 17px)",
});

const DialogActionsStyled = styled("div")({
  marginTop: "10px",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
});

const DialogTitleStyled = styled("div")({
  padding: "5px",
  backgroundColor: "transparent",
  color: "white",
  fontSize: "clamp(16px, min(2.2vw, 4vh), 30px)",
  fontWeight: "bold",
  textAlign: "center",
  marginBottom: "clamp(5px, 1.5vh, 15px)",
});

export const animateTitleStyles = (index) => ({
  opacity: 0,
  color: "orange",
  fontWeight: "bold",
  fontSize: "clamp(18px, min(2.5vw, 4.5vh), 32px)",
  display: "inline-block",
  transform: "translateY(-20px)",
  animation: `fadeIn 0.3s ease-out forwards ${index * 0.4}s`,
});

const Instructions = () => {
  const [userInfo] = useState(GetUserInfo());
  const [checked, setChecked] = useState(false);
  const [isStarting, setIsStarting] = useState(false);
  const navigate = useNavigate();

  const handleCheckboxChange = (event) => setChecked(event.target.checked);

  useEffect(() => {
    const token = sessionStorage.getItem("token");
    const user = GetUserInfo();
    if (!token || !user) {
      navigate("/login");
    }
  }, [navigate]);

  useEffect(() => {
    const img = new Image();
    img.src = require("../../assets/images/Question.webp");
  }, []);
  const animateTitle = (title) =>
    title.split(" ").map((word, index) => (
      <span key={index} style={animateTitleStyles(index)}>
        {word}&nbsp;
      </span>
    ));

  const startExam = async () => {
    if (isStarting) return;
    setIsStarting(true);

    // Clean up any stale exam state before creating new one
    sessionStorage.removeItem("ExamId");
    sessionStorage.removeItem("ExamQuestions");
    sessionStorage.removeItem("SelectedAnswers");
    sessionStorage.removeItem("ExamStartTime");

    const userId = userInfo?.UserId || userInfo?.Id || 0;
    const startExamPayload = {
      EventDetailId: 1,
      UserId: userId,
      StartTime: moment().format("YYYY-MM-DD HH:mm:ss"),
      CreatedBy: userId,
    };

    try {
      let examResponse = await StartExam(startExamPayload);
      if (examResponse && examResponse.success && examResponse.data?.[0]?.Id) {
        if (document.documentElement.requestFullscreen) {
          document.documentElement.requestFullscreen({ navigationUI: "hide" })
            .then(() => {
              if (navigator.keyboard && navigator.keyboard.lock) {
                navigator.keyboard.lock();
              }
            })
            .catch((err) => console.log("Fullscreen request error:", err));
        }
        sessionStorage.setItem("ExamId", examResponse.data[0].Id);
        navigate("/questions");
      } else {
        setIsStarting(false);

        // Token expired or invalid — clear session and redirect to login
        if (examResponse?.status === 498 || examResponse?.status === 401) {
          sessionStorage.clear();
          await Swal.fire({
            icon: "warning",
            title: "Session Expired",
            text: "Your session has expired. Please log in again to continue.",
            confirmButtonColor: "#F59E0B",
            allowOutsideClick: false,
          });
          navigate("/login");
          return;
        }

        Swal.fire({
          icon: "error",
          title: "Failed to Start Exam",
          text: examResponse?.message || "Could not initialize exam session. Please check your connection and try again.",
          confirmButtonColor: "#F59E0B",
        });
      }
    } catch (err) {
      setIsStarting(false);
      Swal.fire({
        icon: "error",
        title: "Connection Error",
        text: "Network error while starting exam. Please check your internet connection.",
        confirmButtonColor: "#F59E0B",
      });
    }
  };

  return (
    <>
      <VideoBackground
        autoPlay
        loop
        muted
        playsInline
        src={backgroundVideo}
        type="video/mp4"
      />
      <Overlay />

      <Container>
        <DialogTitleStyled>{animateTitle("Instructions...")}</DialogTitleStyled>

        <DialogContentStyled>
          <ul>
            <li>
              Embrace the challenge – this assessment won't launch any rockets,
              but it might launch your career!
            </li>
            <li>
              Mandatory fun alert: answering every question is the secret code
              to success. No skipping!
            </li>
            <li>
              Do not unnecessarily smile at the person sitting next to you, they
              may also not know the answer — this is not the right place for
              networking. Try LinkedIn!
            </li>
            <li>
              In case of mental exhaustion, pause, take a deep breath, and
              imagine your favorite meme. Ready? Back to reality!
            </li>
            <li>
              If you hear strange noises during the exam, it's not a ghost; it's
              probably just your neighbors in horror. Stay unfazed.
            </li>
            <li>
              🕐 <b>Time Limit:</b> The exam has a 30-minute time limit. Please manage your time wisely across all questions.
            </li>
            <li>
              ⚠️ <b>WARNING:</b> Avoid switching between tabs or minimizing the browser. You will receive one warning, and a second attempt will result in your test being <b>automatically submitted</b>.
            </li>
             <li>
              📵 <b>Mobile phones are strictly prohibited</b> during the exam. Keep your phone away and switched to silent.
            </li>
            <li>
              ALERT: Once you log out, there's no second chance. Please keep
              this in mind.
            </li>
          </ul>

          {/* Network Issue Notice */}
          <div style={{
            marginTop: "20px",
            padding: "15px 20px",
            backgroundColor: "rgba(245, 158, 11, 0.15)",
            border: "1px solid rgba(245, 158, 11, 0.5)",
            borderRadius: "10px",
            display: "flex",
            alignItems: "flex-start",
            gap: "12px"
          }}>
            <span style={{ fontSize: "clamp(18px, 1.5vw, 24px)" }}>🛟</span>
            <div>
              <p style={{ 
                margin: 0, 
                color: "#F59E0B", 
                fontWeight: "600", 
                fontSize: "clamp(13px, 1.2vw, 16px)",
                marginBottom: "4px"
              }}>
                Need Help?
              </p>
              <p style={{ 
                margin: 0, 
                color: "rgba(255, 255, 255, 0.9)", 
                fontSize: "clamp(12px, 1.1vw, 15px)",
                lineHeight: "1.5"
              }}>
                If you are facing any network issues or any problems with the application, please contact the <b>Admin</b> or <b>Invigilator</b> immediately for assistance.
              </p>
            </div>
          </div>

          <div style={{marginTop: "30px", marginBottom: "40px"}}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={checked}
                  onChange={handleCheckboxChange}
                  style={{color: "orange"}}
                />
              }
              label={
                <h4 style={{ fontSize: "clamp(13px, 1.3vw, 18px)", margin: 0, fontWeight: "500" }}>
                  In the spirit of fairness, you acknowledge that success is
                  directly proportional to the quality of your answers and
                  inversely proportional to the number of coffee stains on your
                  answer sheet.
                </h4>
              }
            />

            <div style={{minHeight: "25px"}}></div>
          </div>
        </DialogContentStyled>

        <DialogActionsStyled>
          {checked && (
            <StartButton text="Proceed to Exam" onClick={startExam} />
          )}
        </DialogActionsStyled>
        <div style={{ position: "fixed", bottom: "10px", right: "16px", fontSize: "12px", color: "rgba(255,255,255,0.6)" }}>
          © 2026 Sunware Technologies. All rights reserved.
        </div>
      </Container>
    </>
  );
};

export default Instructions;
